import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import {
  BUTTON_BORDER_RADIUS,
  BUTTON_HEIGHT,
  BUTTON_WIDTH,
  CircleWithArrowButton,
} from '@/components/shared/CircleWithArrowButton'

import { trackCtaClick } from '@/lib/gtm/gtmV2'

import { CARD_HEIGHT, GutShotGuideSectionCard } from './GutShotGuideSectionCard'

const StyledFeatures = styled.div`
  position: relative;
`

const StyledNavigationRightButton = styled.div`
  border-radius: ${BUTTON_BORDER_RADIUS};
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.24);
  height: ${BUTTON_HEIGHT}px;
  position: absolute;
  right: 32px;
  top: ${(CARD_HEIGHT - BUTTON_HEIGHT) / 2}px;
  width: ${BUTTON_WIDTH}px;

  @media screen and (max-width: ${(p) => p.theme.breakpoints.tablet}) {
    display: none;
  }
`

const StyledNavigationLeftButton = styled(StyledNavigationRightButton)`
  left: 32px;
`

const GAP_BETWEEN_CARDS = 12

const StyledFeatureCards = styled.ul`
  align-items: flex-start;
  display: flex;
  gap: ${GAP_BETWEEN_CARDS}px;
  list-style: none;
  overflow: auto;
  padding: 0 0 32px;
  margin: 0;

  @media screen and (min-width: ${(p) => p.theme.breakpoints.tablet}) {
    overflow: hidden;
    scrollbar-width: none;
    ::-webkit-scrollbar {
      display: none;
    }
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoints.tablet}) {
    & > :first-child {
      margin-left: 24px;
    }

    & > :last-child {
      margin-right: 24px;
    }
  }
`

const GUIDE_FEATURES = [
  {
    title: 'Expert advice from our scientists',
    description: 'We run the world’s largest nutritional study, and you’ll learn from our research.',
  },
  {
    title: '5 steps to improving gut health',
    description: 'We’ll share 5 simple, actionable steps you can take to start feeling your best.',
  },
  {
    title: 'Recipes and a shopping list',
    description: 'Try our delicious recipes and pick up ingredients that’ll boost your gut health.',
  },
  {
    title: 'Recommended swaps',
    description: 'Find out more about how you can swap foods out to give your gut a boost.',
  },
]

const FIRST_POSITION = 0
const LAST_POSITION = 2

const updateButtonVisibility = (position: number, leftButton: HTMLDivElement, rightButton: HTMLDivElement) => {
  const isFirstPosition = position === FIRST_POSITION
  const isLastPosition = position === LAST_POSITION

  leftButton.style.visibility = isFirstPosition ? 'hidden' : 'visible'
  rightButton.style.visibility = isLastPosition ? 'hidden' : 'visible'
}

export const GutShotGuideSectionFeatures = () => {
  const [currentPosition, setCurrentPosition] = useState(0)
  const scrollableComponentRef = useRef<HTMLUListElement>(null)
  const leftButtonRef = useRef<HTMLDivElement>(null)
  const rightButtonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const leftButton = leftButtonRef.current
    const rightButton = rightButtonRef.current

    if (leftButton && rightButton) {
      updateButtonVisibility(currentPosition, leftButton, rightButton)
    }
  }, [currentPosition])

  const handleOnNavigationClick = (index: number) => {
    const scrollableFeaturesList = scrollableComponentRef.current
    const feature = scrollableFeaturesList?.children[index]
    feature?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
    setCurrentPosition(index)
    trackCtaClick('gut_shot', `scroll_${index === FIRST_POSITION ? 'left' : 'right'}`)
  }

  return (
    <StyledFeatures>
      <StyledNavigationRightButton ref={rightButtonRef}>
        <CircleWithArrowButton direction="right" onClick={() => handleOnNavigationClick(LAST_POSITION)} />
      </StyledNavigationRightButton>
      <StyledNavigationLeftButton ref={leftButtonRef}>
        <CircleWithArrowButton direction="left" onClick={() => handleOnNavigationClick(FIRST_POSITION)} />
      </StyledNavigationLeftButton>
      <StyledFeatureCards ref={scrollableComponentRef}>
        {GUIDE_FEATURES.map((feature) => (
          <GutShotGuideSectionCard key={feature.title} title={feature.title} description={feature.description} />
        ))}
      </StyledFeatureCards>
    </StyledFeatures>
  )
}
